import type React from "react";
import { isGuestTier } from "@salestrak/permissions";
import { Box, Text } from "@salestrak/ui";

import { useAppData } from "#app/lib/data";
import {
  checkPermission,
  useViewerCanBrowseMembers,
  useViewerIsShareOnly,
} from "#app/lib/hooks/use-permission";
import { PageLoading } from "#app/components/loading/page-loading";

interface RequirePermissionProps {
  perm: string;
  children: React.ReactNode;
}

/**
 * Full-page "You do not have access" state. Used by RequirePermission and
 * by object-level guards (e.g. a workflow route whose workflow isn't in
 * the viewer's visible set).
 */
export function AccessDenied() {
  return (
    <Box
      fill="flex"
     
      direction="column"
      align="center"
      justify="center"
    >
      <Text variant="muted">You do not have access</Text>
    </Box>
  );
}

/**
 * Route-level permission guard. Wraps a gated screen and checks the
 * permission on load — the sidebar hides gated nav items, but screens
 * are still reachable by URL or in-app navigation (e.g. profile/team
 * dialogs), so every gated route must also verify access itself.
 *
 * - While the workspace `me` payload is loading OR not yet present →
 *   spinner. Gating on `isLoading` alone isn't enough: on a workspace
 *   switch `currentUser` stays populated with the PREVIOUS workspace's
 *   permissions while the refetch is in flight, so evaluating the gate
 *   then would decide against stale data. Waiting for `!isLoading` with a
 *   present `currentUser` guarantees we check the right workspace.
 * - No permission → full-page "You do not have access"
 * - Otherwise → renders the screen
 */
export function RequirePermission({ perm, children }: RequirePermissionProps) {
  const { currentUser, isLoading } = useAppData();

  if (isLoading || !currentUser) {
    return (
      <PageLoading delayed />
    );
  }

  if (!checkPermission(currentUser?.permissions, perm)) {
    return <AccessDenied />;
  }

  return <>{children}</>;
}

/**
 * Route-level guard for the member surfaces — the members directory and the
 * org chart. A share-only viewer sees neither (`useViewerCanBrowseMembers`);
 * the Teams sidebar hides their rows, but both stay reachable by URL, so the
 * routes verify for themselves. Same bootstrap wait as `RequirePermission`:
 * the flag rides the workspace `me` payload, and on a workspace switch the
 * previous workspace's row lingers until the refetch lands.
 */
export function RequireMemberBrowse({ children }: { children: React.ReactNode }) {
  const { currentUser, isLoading } = useAppData();
  const canBrowse = useViewerCanBrowseMembers();

  if (isLoading || !currentUser) {
    return <PageLoading delayed />;
  }

  if (!canBrowse) {
    return <AccessDenied />;
  }

  return <>{children}</>;
}

/**
 * Route-level guard for screens that belong to INTERNAL viewers — every
 * non-Guest, whatever their keys: the read-only vocabulary settings pages
 * (Types, Sources, Outcomes), where the page itself drops its edit
 * affordances without the manage keys. A Limited Member is internal and
 * passes; the member MODULES sit behind `RequireMemberModule` instead, which
 * reads the wider share-only tier. Same bootstrap wait as
 * `RequirePermission`.
 */
export function RequireInternal({ children }: { children: React.ReactNode }) {
  const { currentUser, isLoading } = useAppData();

  if (isLoading || !currentUser) {
    return <PageLoading delayed />;
  }

  if (isGuestTier(currentUser.roles)) {
    return <AccessDenied />;
  }

  return <>{children}</>;
}

/** HOC variant of `RequireInternal` for route components. */
export function withInternal<P extends object>(
  Component: React.ComponentType<P>,
): (props: P) => React.ReactElement {
  function InternalGuardedScreen(props: P) {
    return (
      <RequireInternal>
        <Component {...props} />
      </RequireInternal>
    );
  }
  return InternalGuardedScreen;
}

/**
 * Route-level guard for the member modules — Map, Workflows, Teams,
 * Dashboards (`tier: "member"` in the rail's tile registry). A share-only
 * viewer (`useViewerIsShareOnly`: Guest, Limited Member, a custom role
 * without public access) sees no tile for them and must not reach them by
 * URL either; this is the route half of `tileVisibleToViewer`'s reading. A
 * shared workflow's board is NOT behind it: that route is a share-only
 * viewer's destination via Home. Same bootstrap wait as `RequirePermission`:
 * the flag rides the workspace `me` payload.
 */
export function RequireMemberModule({ children }: { children: React.ReactNode }) {
  const { currentUser, isLoading } = useAppData();
  const shareOnly = useViewerIsShareOnly();

  if (isLoading || !currentUser) {
    return <PageLoading delayed />;
  }

  if (shareOnly) {
    return <AccessDenied />;
  }

  return <>{children}</>;
}

/** HOC variant of `RequireMemberModule` for route components. */
export function withMemberModule<P extends object>(
  Component: React.ComponentType<P>,
): (props: P) => React.ReactElement {
  function MemberModuleGuardedScreen(props: P) {
    return (
      <RequireMemberModule>
        <Component {...props} />
      </RequireMemberModule>
    );
  }
  return MemberModuleGuardedScreen;
}

/**
 * HOC variant for route components:
 * `export default withPermission("manage.users", MembersPage);`
 */
export function withPermission<P extends object>(
  perm: string,
  Component: React.ComponentType<P>,
): (props: P) => React.ReactElement {
  function PermissionGuardedScreen(props: P) {
    return (
      <RequirePermission perm={perm}>
        <Component {...props} />
      </RequirePermission>
    );
  }
  return PermissionGuardedScreen;
}
